import { keepPreviousData, useQuery } from '@tanstack/react-query';
import getFormFieldValues from '../lib/getFormFieldValues';
import getFormVariantValues from '../lib/getFormVariantValues';
import useCheckoutState from './useCheckoutState';
import useSelectedPrice from './useSelectedPrice';
import { isRecurringPurchase } from '../lib/multiPrice';
import api from '../api';
import getTaxAmounts from '../lib/getTaxAmounts';
import getTotalTaxAmounts from '../lib/getTotalTaxAmounts';
import { taxCalculationQueryKey } from '../queryKeys';
import getTicketTypeValues from '../lib/getTicketTypeValues';
import getTicketPwywAmountValues from '../lib/getTicketPwywAmountValues';
import getOrderBumpValues from '../lib/getOrderBumpValues';
import useDebounce from './useDebounce';
import checkIsCoverProcessingFeeSelected from '../lib/checkCoverProcessingFee';

export function useCalculateTaxQuery(
  {
    sellerId,
    pageId,
    priceId,
    fields,
    variants,
    tickets,
    ticketPwywAmounts,
    couponId,
    customerId,
    dynamicPrice,
    payWhatYouWantPrice,
    selectedPaymentMethod,
    orderBumps,
    coverProcessingFee = false,
  },
  taxEnabled,
) {
  const queryKey = taxCalculationQueryKey({
    sellerId,
    pageId,
    priceId,
    fields,
    variants,
    tickets,
    ticketPwywAmounts,
    couponId,
    customerId,
    dynamicPrice,
    payWhatYouWantPrice,
    selectedPaymentMethod,
    orderBumps,
  });

  return useQuery({
    queryKey,
    queryFn: () =>
      api.calculateTax(
        {
          priceId,
          fields,
          variants,
          tickets,
          ticketPwywAmounts,
          couponId,
          customerId,
          dynamicPrice,
          payWhatYouWantPrice,
          selectedPaymentMethod,
          // Absent key (not []) when bump-free — byte parity with today's body.
          ...(orderBumps?.length > 0 && { orderBumps }),
          // Not in the query key: the contribution is added after tax and never taxed, so
          // ticking the box must not buy another Stripe Tax calculation.
          coverProcessingFee,
        },
        sellerId,
        pageId,
      ),
    enabled: taxEnabled && !!pageId && !!sellerId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    keepPreviousData: keepPreviousData,
  });
}

export default function useTaxCalculation(taxEnabled) {
  const {
    page,
    checkout,
    seller,
    form,
    product,
    coupon,
    isUpsell,
    upsell,
    dynamicPrice,
    selectedPaymentMethod,
    payWhatYouWantPrice,
    effectiveSelectedPriceId,
    paymentOption,
  } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  const taxMode = page.tax?.mode || 'stripe';
  const isFixedTaxMode = taxEnabled && taxMode === 'fixed';

  // Multi-price products resolve to no price until the buyer makes a choice
  // (useSelectedPrice / useProductPrice return null/0 — no runtime default
  // fallback). The tax calc must honour the same rule: with no selection there
  // is no taxable base, so skip the API (which would otherwise default to the
  // first/default price on the server and inflate the total) and report zeros.
  const isMultiPrice = Array.isArray(product?.prices) && product.prices.length > 0;
  const hasNoSelectedPrice = isMultiPrice && !selectedPrice;

  const variants = getFormVariantValues(
    product.variants,
    form.variants,
    undefined,
    effectiveSelectedPriceId,
  );
  const { fields } = getFormFieldValues(form.fields, page.fields);

  const debouncedFields = useDebounce(fields, 1000);

  const tickets = getTicketTypeValues(checkout.ticketGroups, form.ticketGroups);

  // Debounced like `fields` above — the amount changes on every keystroke in
  // the pwyw input, and each distinct value is a new query key (a tax API
  // call, and a billable Stripe Tax calculation server-side).
  const ticketPwywAmounts = useDebounce(
    getTicketPwywAmountValues(
      checkout.ticketGroups,
      form.ticketGroups,
      page.eventDetails?.currency || product?.currency,
    ),
    1000,
  );

  // Only the typed amounts wait, as ticketPwywAmounts do: ticking a bump still
  // reprices at once.
  const orderBumpAmounts = useDebounce(form.orderBumpAmounts, 1000);
  const orderBumps = getOrderBumpValues(
    form.orderBumps,
    orderBumpAmounts,
    page.eventDetails?.currency || product?.currency,
  );

  const couponId = coupon?._id;

  // Fixed tax is calculated locally in useCartCost from the hydrated rate — no API call needed.
  const { data, isRefetching } = useCalculateTaxQuery(
    {
      // Tax preview follows the selected price's billing type (legacy fallback).
      // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchase)
      productType: isRecurringPurchase(selectedPrice, product) ? 'subscription' : 'charge',
      sellerId: seller?._id,
      pageId: checkout?._id,
      // Send the SELECTED price so the server taxes the chosen price. Without it,
      // calculate-tax falls back to the default/first price and the total leaks
      // (e.g. a $0 selection shows the default price's amount). Null for legacy
      // single-price → the server synthesises the lone price.
      priceId: effectiveSelectedPriceId,
      fields: debouncedFields,
      variants,
      tickets,
      ticketPwywAmounts,
      dynamicPrice,
      couponId,
      customerId: isUpsell ? upsell?.customerId : null,
      payWhatYouWantPrice,
      selectedPaymentMethod,
      orderBumps,
      coverProcessingFee: checkIsCoverProcessingFeeSelected({ page, form, paymentOption }),
    },
    taxEnabled && !isFixedTaxMode && !hasNoSelectedPrice,
  );

  // No selection → no taxable base. Return zeros rather than the query's
  // kept-previous data (which would otherwise leak the last selection's tax
  // after the buyer deselects).
  const taxData = hasNoSelectedPrice ? undefined : data;

  const recurringTaxAmounts = getTaxAmounts(taxData?.recurringTaxAmounts);
  const recurringTaxAmountsBeforeProration = getTaxAmounts(
    taxData?.recurringTaxAmountsBeforeProration,
  );
  const oneTimeTaxAmounts = getTaxAmounts(taxData?.oneTimeTaxAmounts);
  const totalTaxAmounts = getTotalTaxAmounts(recurringTaxAmounts, oneTimeTaxAmounts);
  const totalTaxAmountsBeforeProration = getTotalTaxAmounts(
    recurringTaxAmountsBeforeProration,
    oneTimeTaxAmounts,
  );

  return {
    recurringTaxAmount: taxData?.recurringTaxAmount || 0,
    recurringAmountExcludingTax: taxData?.recurringAmountExcludingTax || 0,
    recurringTaxAmounts,

    recurringTaxAmountBeforeProration: taxData?.recurringTaxAmountBeforeProration || 0,
    recurringAmountExcludingTaxBeforeProration:
      taxData?.recurringAmountExcludingTaxBeforeProration || 0,
    recurringTaxAmountsBeforeProration,

    oneTimeTaxAmount: taxData?.oneTimeTaxAmount || 0,
    oneTimeTotalAmountExcludingTax: taxData?.oneTimeTotalAmountExcludingTax || 0,
    oneTimeTaxAmounts,
    oneTimeTotalIncludingTax: taxData?.oneTimeTotal || 0,

    totalTaxAmounts,
    totalTaxAmountsBeforeProration,
    // Present only when the server taxed a per-line list (an order-bump
    // purchase in stripe mode) — undefined otherwise, so bump-free consumers
    // see no new data.
    taxBreakdownByLine: taxData?.taxBreakdownByLine,
    isCalculatingTax: isRefetching,
  };
}
